import { Component, ElementRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import {
  DesktopLicenseInvoiceDetails,
  DesktopInvoiceDetails,
  InvoicesService,
} from 'src/app/services/invoices.service';
import {
  DesktopLicenseWithProduct,
  LicenseDurationOptionsResponse,
  LicensesService,
} from 'src/app/services/licenses.service';
import { ToastService } from 'src/app/services/toast.service';

type LicenseOption = {
  details: DesktopLicenseInvoiceDetails;
  showDurationOptions: boolean;
  deleting?: boolean;
  changingDuration?: boolean;
  errorChangingDuration?: string;
  variantOptions?: ProductVariantOption[];
};

type ProductVariantOption = LicenseDurationOptionsResponse & {
  selected: boolean;
};

@Component({
  selector: 'app-cart',
  templateUrl: './cart.component.html',
  styleUrl: './cart.component.scss',
})
export class CartComponent {
  @ViewChild('removeChildrenModal')
  removeChildrenModal!: ElementRef<HTMLDialogElement>;
  @ViewChild('addParentsModal')
  addParentsModal!: ElementRef<HTMLDialogElement>;
  @ViewChild('confirmChangesModal')
  confirmChangesModal!: ElementRef<HTMLDialogElement>;
  @ViewChild('confirmInitialOptionsModal')
  confirmInitialOptionsModal!: ElementRef<HTMLDialogElement>;
  @ViewChild('cannotRemoveAllModal')
  cannotRemoveAllModal!: ElementRef<HTMLDialogElement>;

  loadingInvoice = false;
  invoice?: DesktopInvoiceDetails;
  errorFetchingInvoice = '';
  licenseList: LicenseOption[] = [];

  licenseDurationOptions: Map<string, ProductVariantOption[]> = new Map();

  fetchingProductVariantDurations = false;
  errorFetchingProductVariantDurations = '';

  savingChanges = false;
  errorSavingChanges = '';

  deletingLicense = false;
  changingLicenseDuration = false;

  // editingLicense: LicenseOption | null = null;
  // associatedLicenses: LicenseOption[] = [];

  // updatingLicenseDuration = false;
  // errorUpdatingLicenseDuration = '';

  // currentLicenseToRemove?: LicenseOption;
  // currentChildLicensesToRemove?: LicenseOption[];

  // currentLicenseToAdd?: LicenseOption;
  // currentParentLicensesToAdd?: LicenseOption[];

  constructor(
    private invoicesService: InvoicesService,
    private licensesService: LicensesService,
    private router: Router,
    private toastService: ToastService
    // private authService: AuthService,
    // private cartService: CartService,
    // private route: ActivatedRoute,
  ) {}

  ngOnInit() {
    this.fetchPendingInvoice();
  }

  async fetchPendingInvoice() {
    this.loadingInvoice = true;

    try {
      const pendingInvoice = await this.invoicesService.fetchPendingInvoice();

      if (!pendingInvoice) {
        console.log('no pending invoice');
        return;
      }

      if (pendingInvoice.desktop_invoice.use_manual_values) {
        console.log('invoice uses manual values, proceed to checkout');
        this.router.navigate(['/checkout', pendingInvoice.desktop_invoice.id]);
        return;
      }

      if (pendingInvoice.desktop_invoice.initialized) {
        console.log('invoice is initialized, proceed to checkout');
        this.router.navigate(['/checkout', pendingInvoice.desktop_invoice.id]);
        return;
      }

      this.invoice = pendingInvoice;
      this.initializeLicenseLists(pendingInvoice);
      this.fetchProductVariantDurations();
    } catch (err) {
      console.error(err);
    } finally {
      this.loadingInvoice = false;
    }
  }

  fetchProductVariantDurations() {
    console.log('fetching product variant durations');
    this.fetchingProductVariantDurations = true;
    this.errorFetchingProductVariantDurations = '';

    let cnt = this.licenseList.length;

    for (const license of this.licenseList) {
      // check cached license duration options
      if (
        this.licenseDurationOptions.has(license.details.license_with_product.id)
      ) {
        license.variantOptions = this.licenseDurationOptions.get(
          license.details.license_with_product.id
        )!;
        cnt--;
        if (cnt <= 0) {
          console.log('done fetching product variant durations');
          this.fetchingProductVariantDurations = false;
        }
        continue;
      }

      console.log('fetching product variant durations for license: ', license);
      this.licensesService
        .getDurationOptions(license.details.license_with_product.id)
        .subscribe({
          next: (res) => {
            cnt--;
            console.log('duration options response for license', license, res);

            const options = res.map((opt) =>
              Object.assign(
                {
                  selected:
                    opt.product_variant.id ===
                    license.details.license_with_product.product_variant_id,
                },
                opt
              )
            );
            license.variantOptions = options;
            // cache the options based on license id
            this.licenseDurationOptions.set(
              license.details.license_with_product.id,
              options
            );

            if (cnt <= 0) {
              console.log('done fetching product variant durations');
              this.fetchingProductVariantDurations = false;
            }
          },
          error: (err) => {
            cnt--;
            console.error(err);
            if (cnt <= 0) {
              console.log('done fetching product variant durations');
              this.fetchingProductVariantDurations = false;
            }
            if (this.errorFetchingProductVariantDurations === '') {
              this.errorFetchingProductVariantDurations =
                'Error fetching product variant duration options';
            }
          },
        });
    }
  }

  initializeLicenseLists(invoice: DesktopInvoiceDetails) {
    this.licenseList = [];

    console.log('invoice license details');
    for (const license of invoice.license_details) {
      const l = license.license_with_product;
      console.log(
        'license: ',
        l.id,
        l.product.title,
        l.parent_desktop_license_id
      );
    }

    const sortedLicenses = this.licensesService.sortLicensesByRelationship(
      invoice.license_details.map((l) => l.license_with_product)
    );

    for (const license of sortedLicenses) {
      console.log('sorted license: ', license.id, license.product.title);
      const details = invoice.license_details.find(
        (l) => l.license_with_product.id === license.id
      );
      if (details) {
        this.licenseList.push({
          details: details,
          showDurationOptions: false,
        });
      }
    }
  }

  getSelectedVariant(option: LicenseOption) {
    if (!option.variantOptions) {
      return null;
    }
    for (const variant of option.variantOptions) {
      if (variant.selected) {
        return variant.product_variant;
      }
    }
    return null;
  }

  getLicenseType(license: DesktopLicenseWithProduct) {
    return this.licensesService.getType(license);
  }

  hasThreeYearOption(license: LicenseOption) {
    if (!license.variantOptions) {
      return false;
    }
    return license.variantOptions.some(
      (v) => v.product_variant.duration_months === 36
    );
  }

  updateLicenseDurationVariant(
    license: LicenseOption,
    newOption: ProductVariantOption
  ) {
    license.errorChangingDuration = '';

    // get the original selection to fall back on in case of error
    const originalSelection = this.getSelectedVariant(license);

    if (!originalSelection) {
      console.error(new Error('No original selection for license'));
      license.errorChangingDuration = 'Error changing license duration';
      return;
    }

    if (originalSelection.id === newOption.product_variant.id) {
      console.log('No change to license duration');
      return;
    }

    for (const option of license.variantOptions!) {
      option.selected = false;
    }
    newOption.selected = true;

    license.changingDuration = true;
    license.errorChangingDuration = '';
    this.changingLicenseDuration = true;
    this.invoicesService
      .updateLicenseProductVariant({
        invoice_id: this.invoice!.desktop_invoice.id,
        variant_updates: [
          {
            desktop_license_id: license.details.license_with_product.id,
            new_product_variant_id: newOption.product_variant.id,
          },
        ],
      })
      .subscribe({
        next: (invoice) => {
          license.changingDuration = false;
          this.changingLicenseDuration = false;
          this.invoice = invoice.invoice_details;
          this.initializeLicenseLists(this.invoice);
          this.fetchProductVariantDurations();
        },
        error: (err) => {
          license.changingDuration = false;
          this.changingLicenseDuration = false;
          // reset the selected variant to the original selection
          for (const option of license.variantOptions!) {
            option.selected =
              originalSelection?.id === option.product_variant.id;
          }
          license.errorChangingDuration = 'Error changing license duration';
          console.error(err);
        },
      });

    // also find the matching variant for this license's children, and apply
    // that to the newVariant of the child
    // const children = this.getChildLicenses(this.licenseList, license);
    // for (const child of children) {
    //   if (!child.variantOptions) continue;
    //   for (const variantOption of child.variantOptions) {
    //     if (
    //       variantOption.product_variant.duration_months ===
    //       newOption.product_variant.duration_months
    //     ) {
    //       variantOption.selected = true;
    //     } else {
    //       variantOption.selected = false;
    //     }
    //   }
    // }
  }

  deleteLicense(license: LicenseOption) {
    license.deleting = true;
    this.deletingLicense = true;
    this.invoicesService
      .removeLicenseFromInvoice(
        this.invoice!.desktop_invoice.id,
        license.details.license_with_product.id
      )
      .subscribe({
        next: (invoice) => {
          license.deleting = false;
          this.deletingLicense = false;
          if (invoice == null) {
            // invoice was deleted
            console.log('no invoice returned');
            this.invoice = undefined;
            this.licenseList = [];
            return;
          }
          this.invoice = invoice.invoice_details;
          this.initializeLicenseLists(this.invoice);
          this.fetchProductVariantDurations();
        },
        error: (err) => {
          license.deleting = false;
          this.deletingLicense = false;
          this.toastService.addToast(
            'error',
            'Error removing license from cart'
          );
          console.error(err);
        },
      });
  }

  getChildLicenses(
    licenses: LicenseOption[],
    currentLicense: LicenseOption
  ): LicenseOption[] {
    const newList: LicenseOption[] = [];

    const children = licenses.filter(
      (l) =>
        l.details.license_with_product.parent_desktop_license_id ===
        currentLicense.details.license_with_product.id
    );
    for (const child of children) {
      newList.push(child);
      const grandChildren = this.getChildLicenses(licenses, child);
      newList.push(...grandChildren);
    }

    return newList;
  }

  getParentLicenses(
    licenses: LicenseOption[],
    currentLicense: LicenseOption
  ): LicenseOption[] {
    const newList: LicenseOption[] = [];

    const parent = licenses.find(
      (l) =>
        l.details.license_with_product.id ===
        currentLicense.details.license_with_product.parent_desktop_license_id
    );
    if (parent) {
      newList.push(parent);
      const grandParents = this.getParentLicenses(licenses, parent);
      newList.push(...grandParents);
    }

    return newList;
  }

  goToCheckout() {
    this.router.navigate(['/checkout', this.invoice!.desktop_invoice.id]);
  }

  // get hasChanges() {
  //   return this.licenseList.some(
  //     (license) => license.remove || this.hasNewVariant(license)
  //   );
  // }

  // hasNewVariant(option: LicenseOption) {
  //   const selectedVariant = this.getSelectedVariant(option);
  //   if (!selectedVariant) {
  //     return false;
  //   }
  //   return (
  //     selectedVariant.id !==
  //     option.details.license_with_product.product_variant_id
  //   );
  // }

  // removeLicense(license: LicenseOption) {
  //   // check if there are child licenses
  //   // only get child licenses that are not being removed
  //   const children = this.getChildLicenses(this.licenseList, license).filter(
  //     (l) => !l.remove
  //   );

  //   if (children.length > 0) {
  //     // set both holding variables to be used if the user confirms removal
  //     this.currentLicenseToRemove = license;
  //     this.currentChildLicensesToRemove = children;
  //     this.removeChildrenModal.nativeElement.showModal();
  //     return;
  //   }

  //   license.remove = true;
  // }

  // removeCurrentChildLicenses() {
  //   // update the currently set parent/children licenses to be removed
  //   if (!this.currentLicenseToRemove) {
  //     console.error(new Error('No parent license to remove'));
  //     this.removeChildrenModal.nativeElement.close();
  //     return;
  //   }

  //   this.currentLicenseToRemove.remove = true;
  //   if (this.currentChildLicensesToRemove) {
  //     for (const license of this.currentChildLicensesToRemove) {
  //       license.remove = true;
  //     }
  //   }

  //   this.removeChildrenModal.nativeElement.close();
  // }

  // cancelRemoveChildLicenses() {
  //   // close modal and reset holding variables
  //   this.removeChildrenModal.nativeElement.close();
  //   this.currentLicenseToRemove = undefined;
  //   this.currentChildLicensesToRemove = undefined;
  // }

  // addLicense(license: LicenseOption) {
  //   // check if license has a parent that is still removed
  //   if (license.details.license_with_product.parent_desktop_license_id) {
  //     const parents = this.getParentLicenses(this.licenseList, license).filter(
  //       (p) => p.remove
  //     );

  //     if (parents.length > 0) {
  //       this.currentLicenseToAdd = license;
  //       this.currentParentLicensesToAdd = parents;
  //       this.addParentsModal.nativeElement.showModal();
  //       return;
  //     }
  //   }

  //   license.remove = false;
  // }

  // addCurrentParentLicenses() {
  //   // update the currently set parent/children licenses to undo removal
  //   if (!this.currentLicenseToAdd) {
  //     console.error(new Error('No parent license to remove'));
  //     this.addParentsModal.nativeElement.close();
  //     return;
  //   }

  //   this.currentLicenseToAdd.remove = false;
  //   if (this.currentParentLicensesToAdd) {
  //     for (const license of this.currentParentLicensesToAdd) {
  //       license.remove = false;
  //     }
  //   }

  //   this.addParentsModal.nativeElement.close();
  // }

  // cancelAddParentLicenses() {
  //   this.currentLicenseToAdd = undefined;
  //   this.currentParentLicensesToAdd = undefined;
  //   this.addParentsModal.nativeElement.close();
  // }

  // back() {
  //   // if not initialized, go back to the licenses page
  //   if (!this.invoice?.desktop_invoice.initialized) {
  //     // if admin user, go back to tenant details page
  //     if (this.authService.isAdmin()) {
  //       this.router.navigate([
  //         '/admin/tenant',
  //         this.invoice!.desktop_invoice.tenant_id,
  //       ]);
  //     } else {
  //       this.router.navigate(['/licenses']);
  //     }
  //     return;
  //   }

  //   // check if there are changes
  //   if (this.hasChanges) {
  //     if (!confirm('Are you sure you want to leave without saving changes?')) {
  //       // do nothing
  //       return;
  //     }
  //   }

  //   // if admin user, go back to tenant details page
  //   if (this.authService.isAdmin()) {
  //     this.router.navigate([
  //       '/admin/tenant',
  //       this.invoice!.desktop_invoice.tenant_id,
  //     ]);
  //   } else {
  //     this.router.navigate(['/invoice', this.invoice!.desktop_invoice.id]);
  //   }
  // }

  // get licensesWithDurationChanges() {
  //   return this.licenseList.filter((l) => this.hasNewVariant(l) && !l.remove);
  // }

  // get licensesToRemove() {
  //   return this.licenseList.filter((l) => l.remove);
  // }

  // promptConfirmChanges() {
  //   if (this.hasChanges) {
  //     this.confirmChangesModal.nativeElement.showModal();
  //   }
  // }

  // closeConfirmChangesModal() {
  //   this.confirmChangesModal.nativeElement.close();
  // }

  // closeCannotRemoveAllModal() {
  //   this.cannotRemoveAllModal.nativeElement.close();
  // }

  // async saveChanges() {
  //   this.closeConfirmChangesModal();

  //   if (!this.invoice) {
  //     console.error(new Error('No invoice to save changes to'));
  //     return;
  //   }

  //   // check that some licenses will remain
  //   if (this.licenseList.filter((l) => !l.remove).length === 0) {
  //     console.error('Cannot save changes. Would remove all licenses');
  //     this.cannotRemoveAllModal.nativeElement.showModal();
  //     return;
  //   }

  //   const invoice = this.invoice;

  //   this.savingChanges = true;
  //   this.errorSavingChanges = '';

  //   // make the renewal change request first. We need to change the renewal
  //   // status before changing the duration if there is changes.
  //   const renewalChangeReq = this.licenseList
  //     .filter((l) => l.remove)
  //     .map((l) => ({
  //       desktop_license_id: l.details.license_with_product.id,
  //       will_renew: false,
  //     }));

  //   if (renewalChangeReq.length > 0) {
  //     try {
  //       await new Promise<UpdateDesktopLicenseWillRenewResponse>(
  //         (resolve, reject) => {
  //           this.licensesService
  //             .updateLicenseWillRenew(renewalChangeReq)
  //             .subscribe({
  //               next: (data) => resolve(data),
  //               error: (err) => reject(err),
  //             });
  //         }
  //       );
  //     } catch (err) {
  //       console.error(err);
  //       this.errorSavingChanges = 'Unexpected error adding/removing licenses';
  //       this.savingChanges = false;
  //       return;
  //     }
  //   }

  //   const durationChanges: ProductVariantUpdate[] = [];
  //   for (const l of this.licensesWithDurationChanges) {
  //     const variant = this.getSelectedVariant(l);
  //     if (!variant) {
  //       console.error(new Error('No variant selected for license'));
  //       this.errorSavingChanges =
  //         'Unexpected error updating licenses for invoice';
  //       this.savingChanges = false;
  //       return;
  //     }
  //     durationChanges.push({
  //       desktop_license_id: l.details.license_with_product.id,
  //       new_product_variant_id: variant.id,
  //     });
  //   }

  //   if (durationChanges.length > 0) {
  //     try {
  //       await new Promise<DesktopInvoiceResponse>((resolve, reject) => {
  //         this.invoicesService
  //           .updateLicenseProductVariant({
  //             invoice_id: invoice.desktop_invoice.id,
  //             variant_updates: durationChanges,
  //           })
  //           .subscribe({
  //             next: (data) => resolve(data),
  //             error: (err) => reject(err),
  //           });
  //       });
  //     } catch (err) {
  //       console.error(err);
  //       this.errorSavingChanges = 'Unexpected error changing license duration';
  //       this.savingChanges = false;
  //       return;
  //     }
  //   }

  //   if (this.invoice.desktop_invoice.initialized) {
  //     // changes are being made to already initialized invoice
  //     // done making changes, show messsage and stay on page
  //     this.savingChanges = false;
  //     this.errorSavingChanges = '';

  //     this.toastService.addToast('success', 'Licenses updated successfully');
  //     // with potentially multiple changes, just refetch the invoice data
  //     this.fetchInvoiceDetails(this.invoice!.desktop_invoice.id);
  //     return;
  //   }

  //   // update the invoice to be initialized
  //   try {
  //     await new Promise<DesktopInvoiceResponse>((resolve, reject) => {
  //       this.invoicesService
  //         .updateInvoiceInitialized(this.invoice!.desktop_invoice.id, true)
  //         .subscribe({
  //           next: (data) => resolve(data),
  //           error: (err) => reject(err),
  //         });
  //     });
  //   } catch (err) {
  //     console.error(err);
  //     this.errorSavingChanges = 'Unexpected error updating invoice status';
  //     this.savingChanges = false;
  //     return;
  //   }

  //   // if changes were successful, browse to the invoice page
  //   if (this.authService.isAdmin()) {
  //     // navigate to the admin invoice page route if user is an admin
  //     this.router.navigate([
  //       '/admin/tenant',
  //       this.invoice!.desktop_invoice.tenant_id,
  //       'invoice',
  //       this.invoice!.desktop_invoice.id,
  //     ]);
  //     return;
  //   } else {
  //     // browse to normal user invoice page for non-admin users
  //     this.router.navigate(['/invoice', this.invoice!.desktop_invoice.id]);
  //   }
  // }
}
